'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import type {
  ConfirmDetectWebAppsBodyDto,
  WebAppClusterDto,
} from '@terra/shared-contracts-external-network';
import type { WebAppDraftStrategyType } from '@terra/shared-schemas-orchestrator';
import { Button } from '../../../../components/button';
import { SettingsBooleanControl } from '../../../settings/SettingsBooleanControl';
import { SettingsCard } from '../../../settings/SettingsCard';
import { SettingsRow } from '../../../settings/SettingsRow';
import { SettingsSection } from '../../../settings/SettingsSection';
import { toasterApi } from '../../../toast/ToastProvider';
import { InventoryInfoAlert } from '../InventoryInfoAlert';
import { notifyInventoryFailure } from '../notify-inventory-failure';
import { WebAppStrategyDialog } from './WebAppStrategyDialog';
import { WebAppsReviewDialog } from './WebAppsReviewDialog';
import {
  countReviewableWebAppClusters,
  countWaitingMatchedWebAppClusters,
  isReviewableWebAppCluster,
  mergePreviewWithPersistedClusters,
  toConfirmDetectWebAppsPayload,
} from './detect-web-apps-confirm';
import { useWebAppReviewState } from './useWebAppReviewState';
import { webAppStrategyTranslationKey } from './web-app-strategy';
import type {
  LinkableWebApplication,
  WebAppDetectionSurfaceUpdate,
  WebAppReviewMode,
} from './web-app-types';

export type WebAppDetectionSettingsSectionProps = {
  detectWebAppsEnabled: boolean;
  webAppDraftStrategy: WebAppDraftStrategyType;
  previewClusters: WebAppClusterDto[] | undefined;
  persistedClusters: WebAppClusterDto[] | undefined;
  clustersLoading: boolean;
  previewFailed: boolean;
  applications: LinkableWebApplication[];
  isReadOnly: boolean;
  isUpdatingSurface: boolean;
  isConfirming: boolean;
  onReviewOpenChange: (open: boolean) => void;
  onUpdateSurface: (dto: WebAppDetectionSurfaceUpdate) => Promise<unknown>;
  onConfirm: (body: ConfirmDetectWebAppsBodyDto) => Promise<unknown>;
};

export function WebAppDetectionSettingsSection({
  detectWebAppsEnabled,
  webAppDraftStrategy,
  previewClusters,
  persistedClusters,
  clustersLoading,
  previewFailed,
  applications,
  isReadOnly,
  isUpdatingSurface,
  isConfirming,
  onReviewOpenChange,
  onUpdateSurface,
  onConfirm,
}: WebAppDetectionSettingsSectionProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalInventory.webApp',
  );
  const tShared = useTranslations(
    'designSystem.networkSettings.externalInventory.shared',
  );
  const tCommon = useTranslations('designSystem.common');
  const [strategyDialogOpen, setStrategyDialogOpen] = useState(false);
  const listedClusters = useMemo(
    () =>
      mergePreviewWithPersistedClusters({
        preview: previewClusters,
        persisted: persistedClusters,
      }),
    [persistedClusters, previewClusters],
  );
  const reviewableClusters = useMemo(
    () => listedClusters.filter(isReviewableWebAppCluster),
    [listedClusters],
  );
  const reviewableCount = countReviewableWebAppClusters(listedClusters);
  const waitingMatchCount = countWaitingMatchedWebAppClusters(listedClusters);
  const {
    reviewMode,
    setReviewMode,
    decisions,
    linkChoices,
    changeDecision,
    changeLinkChoice,
  } = useWebAppReviewState({ clusters: listedClusters, clustersLoading });

  const changeReviewMode = (nextMode: WebAppReviewMode | null) => {
    setReviewMode(nextMode);
    onReviewOpenChange(nextMode !== null);
  };

  const saveSurface = async (dto: WebAppDetectionSurfaceUpdate) => {
    try {
      await onUpdateSurface(dto);
      toasterApi.success({ title: tShared('saveSuccess') });

      return true;
    } catch (error) {
      notifyInventoryFailure({ title: tShared('error'), error });

      return false;
    }
  };

  const handleToggle = (checked: boolean) => {
    if (checked) {
      changeReviewMode('enable');

      return;
    }

    void saveSurface({ detectWebAppsEnabled: false });
  };

  const handleStrategySave = async (strategy: WebAppDraftStrategyType) => {
    const saved = await saveSurface({ webAppDraftStrategy: strategy });

    if (saved) {
      setStrategyDialogOpen(false);
    }
  };

  const handleConfirm = async () => {
    try {
      await onConfirm({
        strategy: webAppDraftStrategy,
        ...toConfirmDetectWebAppsPayload({
          decisions,
          linkChoices,
          enableAutoCreate: reviewMode === 'enable',
        }),
      });
      toasterApi.success({
        title:
          reviewMode === 'enable' ? t('enabledSuccess') : t('reviewSuccess'),
      });
      changeReviewMode(null);
    } catch (error) {
      notifyInventoryFailure({ title: tShared('error'), error });
    }
  };

  return (
    <>
      <SettingsSection
        title={t('settingsTitle')}
        description={t('settingsDescription')}
      >
        {waitingMatchCount > 0 ? (
          <InventoryInfoAlert
            message={t('waitingMatches', { count: waitingMatchCount })}
          />
        ) : null}
        <SettingsCard>
          <SettingsRow
            label={t('reviewLabel')}
            description={t('reviewDescription')}
            control={
              <Button
                size="xs"
                variant="outline"
                disabled={
                  isReadOnly || (!clustersLoading && reviewableCount === 0)
                }
                onClick={() => changeReviewMode('review')}
              >
                {tShared('review')}
              </Button>
            }
          />
          <SettingsRow
            label={t('detectLabel')}
            description={t('detectDescription')}
            control={
              <SettingsBooleanControl
                checked={detectWebAppsEnabled}
                disabled={isUpdatingSurface || isConfirming || isReadOnly}
                label={
                  detectWebAppsEnabled
                    ? tCommon('enabled')
                    : tCommon('disabled')
                }
                onCheckedChange={({ checked }) => handleToggle(checked)}
              />
            }
          />
          <SettingsRow
            label={t('strategyLabel')}
            description={t('strategyDescription')}
            value={t(
              `strategy.${webAppStrategyTranslationKey(webAppDraftStrategy)}`,
            )}
            action={{
              label: tCommon('edit'),
              onClick: () => setStrategyDialogOpen(true),
            }}
            isReadOnly={isReadOnly}
          />
        </SettingsCard>
      </SettingsSection>

      <WebAppStrategyDialog
        open={strategyDialogOpen}
        strategy={webAppDraftStrategy}
        loading={isUpdatingSurface}
        onOpenChange={setStrategyDialogOpen}
        onSave={(strategy) => void handleStrategySave(strategy)}
      />

      <WebAppsReviewDialog
        reviewMode={reviewMode}
        clustersLoading={clustersLoading}
        previewFailed={previewFailed}
        reviewableClusters={reviewableClusters}
        decisions={decisions}
        linkChoices={linkChoices}
        applications={applications}
        isConfirming={isConfirming}
        onOpenChange={(open) =>
          changeReviewMode(open ? (reviewMode ?? 'review') : null)
        }
        onDecisionChange={changeDecision}
        onLinkChoiceChange={changeLinkChoice}
        onSubmit={() => void handleConfirm()}
      />
    </>
  );
}
